"use client";
import React, { useState } from 'react';
import Sidebar from '@/components/Sidebar';
import { Card, CardHeader, CardContent, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';

const ContactPage: React.FC = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState<string | null>(null);

  const handleSubmit = async () => {
    setSending(true);
    setSendResult(null);
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, phone, message }),
      });
      if (res.ok) {
        setSendResult('Your message has been sent');
        setName('');
        setEmail('');
        setPhone('');
        setMessage('');
      } else {
        const data = await res.json();
        setSendResult(data.error || 'Error sending message');
      }
    } catch (err) {
      console.error(err);
      setSendResult('Error sending message');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-background">
      <Sidebar />
      <div className="flex-1 p-6 md:p-10 ml-0 md:ml-64">
        <div className="max-w-5xl mx-auto">
          {/* Hero */}
          <div className="mb-8">
            <h1 className="text-3xl font-semibold tracking-tight">Contact us</h1>
            <p className="text-sm text-muted-foreground mt-1">
              We’re here to help with quotes, jobs, payouts, and anything else you need.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
            {/* Form */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle>Send us a message</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name">Name</Label>
                    <Input id="name" value={name} onChange={(e) => setName(e.target.value)} />
                  </div>
                  <div>
                    <Label htmlFor="email">Email</Label>
                    <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="phone">Phone</Label>
                    <Input id="phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
                  </div>
                  <div>
                    <Label htmlFor="topic">Topic (optional)</Label>
                    <Input id="topic" placeholder="Billing, jobs, verification…" />
                  </div>
                </div>
                <div>
                  <Label htmlFor="message">Message</Label>
                  <Textarea id="message" rows={6} value={message} onChange={(e) => setMessage(e.target.value)} />
                </div>
                <div className="flex justify-end">
                  <Button onClick={handleSubmit} disabled={sending}>
                    {sending ? <Loader2 className="animate-spin h-4 w-4" /> : 'Send message'}
                  </Button>
                </div>
                {sendResult && <p className="text-center text-sm text-muted-foreground">{sendResult}</p>}
              </CardContent>
            </Card>

            {/* Help panel */}
            <Card>
              <CardHeader>
                <CardTitle>Support</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm text-muted-foreground">
                <p>
                  • For payout issues, ensure your Stripe details are complete in Settings.
                </p>
                <p>
                  • For job disputes, provide the job ID and any relevant photos.
                </p>
                <p>
                  • For verification, you can upload documents in Settings anytime.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;